import React, { useEffect, useState } from "react";
import { ChevronRight, Globe } from "lucide-react";

import { ListCard, ListTile } from "@/shared/components/ListCard";
import { Group, SelectInput, StatusBadge } from "@/shared/components/settings-atoms";
import { SearchInput } from "@/shared/components/ui-kit";
import { CHANNELS, effectOf, personName, type Fallback } from "./copy";
import { ToggleChip } from "./ToggleChip";

import type { AgentUser } from "@/shared/types";
import type { Rule, RuleChannel, RuleCheck, RuleCheckBody } from "../../types";

/** Typing settles before an email is checked. */
const SETTLE_MS = 300;
const CHANNEL_OPTIONS = CHANNELS.map(([id, name]) => ({ id, label: `On ${name}` }));

/* Which policy someone meets first, and what it lets them do: an email and the channel they
   write on, or one of the first people who've written. The answer is the server's
   (`POST /agent_policies/rules/check`); pressing it opens that policy. */
export function CheckPerson({ agentId, people, policies, result, fallback, onProbe, onOpen }: {
  agentId: string;
  people: AgentUser[];
  policies: Rule[];
  result: RuleCheck | undefined;
  fallback: Fallback;
  onProbe: (body: RuleCheckBody | null) => void;
  onOpen: (policyId: string | null) => void;
}) {
  const [email, setEmail] = useState("");
  const [channel, setChannel] = useState<RuleChannel>("web");
  const [picked, setPicked] = useState<AgentUser | null>(null);

  useEffect(() => {
    if (picked) return onProbe({ agent_id: agentId, person_id: picked.id });
    const typed = email.trim();
    if (!typed) return onProbe(null);
    const timer = setTimeout(() => onProbe({ agent_id: agentId, email: typed, channel }), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [agentId, email, channel, picked, onProbe]);

  const hit = result && (result.rule_id === null ? null : policies.find((p) => p.id === result.rule_id));
  const n = hit ? policies.indexOf(hit) + 1 : null;
  const why = result && [picked ? personName(picked) : null, sentence(result.reasons.join(" · "))].filter(Boolean).join(" · ");

  return (
    <Group className="mt-9" title="Check a person" desc="See which policy someone meets first, and what it lets them do.">
      <ListCard>
        <div className="px-4">
          <div className="flex flex-wrap items-center gap-2 py-3">
            <SearchInput
              raised
              aria-label="Email to check"
              placeholder="An email, e.g. sam@northwind.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setPicked(null);
              }}
              className="flex-[1_1_220px] min-w-0"
            />
            <SelectInput
              aria-label="Channel they write on"
              compact
              className="shrink-0"
              value={channel}
              options={CHANNEL_OPTIONS}
              onChange={(value) => {
                setChannel(value as RuleChannel);
                setPicked(null);
              }}
            />
          </div>
          {people.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pb-3">
              <span className="mr-0.5 text-[12px] text-[var(--text-tertiary)]">Or someone who's written</span>
              {people.map((p) => {
                const on = picked?.id === p.id;
                return (
                  <ToggleChip
                    key={p.id}
                    on={on}
                    onClick={() => {
                      setPicked(on ? null : p);
                      setEmail("");
                    }}
                  >
                    {personName(p)}
                  </ToggleChip>
                );
              })}
            </div>
          )}
        </div>
        {result && hit !== undefined && (
          <button
            type="button"
            aria-label="Open the policy they meet"
            onClick={() => onOpen(hit?.id ?? null)}
            className="press w-full flex items-center gap-3 pl-4 pr-2 py-3 text-left hover:bg-[var(--accent-hover)]"
          >
            <ListTile className="font-mono tabular-nums text-[12px]">
              {n === null ? <Globe className="w-[14px] h-[14px]" strokeWidth={1.75} /> : n}
            </ListTile>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 min-w-0">
                <span className="min-w-0 truncate text-[13.5px] font-medium text-[var(--text-primary)]">{hit ? hit.name : "Everyone else"}</span>
                <StatusBadge tone={hit?.action === "block" ? "danger" : "success"}>{hit?.action === "block" ? "Keeps out" : "Lets in"}</StatusBadge>
              </span>
              <span className="block mt-px truncate text-[12.5px] text-[var(--text-tertiary)]">{why}</span>
              <span className="block mt-px truncate text-[12.5px] text-[var(--text-secondary)]">{effectOf(hit ?? null, fallback)}</span>
            </span>
            <ChevronRight className="w-[14px] h-[14px] shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
          </button>
        )}
      </ListCard>
    </Group>
  );
}

const sentence = (text: string) => (text ? text[0].toUpperCase() + text.slice(1) : text);
